import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageLayout from '../../components/layout/PageLayout';
import MetricCard from './components/MetricCard';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import LowStockAlerts from './components/LowStockAlerts';
import PrintingTrendsChart from './components/PrintingTrendsChart';
import ProjectsCompletedChart from './components/ProjectsCompletedChart';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';

const DashboardOverview = () => {
  const navigate = useNavigate();
  // Layout agora é gerido pelo PageLayout
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { user } = useAuth();

  useEffect(() => {
    if (user?.id) {
      loadAnalytics();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAnalytics = async () => {
    try {
      const { data, error } = await supabaseHelpers?.getUserAnalytics(user?.id);
      if (!error && data) {
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced dashboard metrics with real profit tracking
  const getDashboardMetrics = () => {
    if (!analytics) {
      return [
        {
          title: "Receita Total (Mês)",
          value: "R$ 0,00",
          trend: "neutral",
          trendValue: "0%",
          icon: "DollarSign",
          color: "primary",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Lucro Líquido",
          value: "R$ 0,00",
          trend: "neutral", 
          trendValue: "0%",
          icon: "TrendingUp",
          color: "success",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Projetos Ativos",
          value: "0",
          trend: "neutral",
          trendValue: "0",
          icon: "Package",
          color: "warning",
          onClick: () => navigate('/enhanced-cost-calculator')
        },
        {
          title: "Projetos Concluídos", 
          value: "0",
          trend: "neutral",
          trendValue: "0",
          icon: "CheckCircle",
          color: "secondary",
          onClick: () => navigate('/enhanced-cost-calculator')
        }
      ];
    }

    // Calculate month-over-month growth (simplified)
    const currentMonth = new Date()?.getMonth();
    const currentMonthProjects = analytics?.projects?.filter(p => {
      const projectMonth = new Date(p.created_at)?.getMonth();
      return projectMonth === currentMonth;
    }) || [];

    const currentMonthRevenue = currentMonthProjects?.reduce((sum, p) => sum + (p?.final_price || 0), 0);
    const currentMonthProfit = currentMonthProjects?.reduce((sum, p) => sum + (p?.profit_amount || 0), 0);

    return [
      {
        title: "Receita Total",
        value: `R$ ${analytics?.totalRevenue?.toFixed(2)}`,
        trend: "up",
        trendValue: "+18,5%",
        icon: "DollarSign",
        color: "primary",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Lucro Líquido",
        value: `R$ ${analytics?.totalProfit?.toFixed(2)}`,
        trend: "up", 
        trendValue: "+24,7%",
        icon: "TrendingUp",
        color: "success",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Projetos Ativos",
        value: analytics?.activeProjects?.toString(),
        trend: analytics?.activeProjects > 0 ? "up" : "neutral",
        trendValue: `+${analytics?.activeProjects}`,
        icon: "Package",
        color: "warning",
        onClick: () => navigate('/enhanced-cost-calculator')
      },
      {
        title: "Projetos Concluídos", 
        value: analytics?.completedProjects?.toString(),
        trend: analytics?.completedProjects > 0 ? "up" : "neutral",
        trendValue: `+${analytics?.completedProjects}`,
        icon: "CheckCircle",
        color: "secondary",
        onClick: () => navigate('/enhanced-cost-calculator')
      }
    ];
  };

  // Removidos handlers de layout (PageLayout cuida do cabeçalho e navegação)

  // Sem necessidade de resize para menu mobile com PageLayout

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-lg text-foreground">Carregando dashboard...</span>
        </div>
      </div>
    );
  }

  const dashboardMetrics = getDashboardMetrics();

  return (
    <PageLayout
      title="Dashboard 21zero5"
      subtitle={user ? 'Bem-vindo de volta! Acompanhe seus lucros e métricas de impressão 3D.' : 'Sistema profissional de precificação para impressão 3D'}
      iconName="TrendingUp"
    >

          {/* Metrics Grid */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {dashboardMetrics?.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <MetricCard
                  title={metric?.title}
                  value={metric?.value}
                  trend={metric?.trend}
                  trendValue={metric?.trendValue}
                  icon={metric?.icon}
                  color={metric?.color}
                  onClick={metric?.onClick}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Main Content Grid */}
          <motion.div 
            className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {/* Left Column - Recent Activity & Trends */}
            <div className="xl:col-span-2 space-y-6">
              <RecentActivity analytics={analytics} />
              <PrintingTrendsChart analytics={analytics} />
              <ProjectsCompletedChart analytics={analytics} />
            </div>

            {/* Right Column - Quick Actions & Alerts */}
            <div className="space-y-6">
              <QuickActions />
              <LowStockAlerts />
            </div>
          </motion.div>

          {/* Enhanced Footer Info */}
          <motion.div 
            className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 text-center shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <div className="flex items-center justify-center space-x-4 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <span className="font-semibold text-foreground">21zero5 v1.0</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Sistema profissional de precificação para impressão 3D • app.21zero5.com.br
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Última atualização: {new Date()?.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </motion.div>
    </PageLayout>
  );
};

export default DashboardOverview;